import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { groupFunds, fundPickerLabel, type RangeFund } from "./rangeFunds";

interface FundPickerProps {
  value: string;
  onValueChange: (v: string) => void;
  funds: RangeFund[];
  placeholder?: string;
  triggerClassName?: string;
  /** Show manager and class alongside the fund name. Batch rows want this; the compact
   *  single-trade dialogs historically showed the bare fund name. */
  verbose?: boolean;
}

/**
 * Fund dropdown for the trade dialogs, sectioned into "currently held" / "previously held —
 * buy back" / "not held here".
 *
 * The previously-held section is the whole point: an exit is a soft delete (a `weight_pct = 0`
 * row), and before this existed those funds were filtered out of every picker, so a sold holding
 * could never be bought back. Showing them — labelled honestly rather than mixed in with live
 * holdings — is what makes the soft delete reversible.
 */
export default function FundPicker({
  value,
  onValueChange,
  funds,
  placeholder = "Select fund...",
  triggerClassName,
  verbose,
}: FundPickerProps) {
  const sections = groupFunds(funds);
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {sections.map((section) => (
          <SelectGroup key={section.group}>
            <SelectLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {section.label}
            </SelectLabel>
            {section.funds.map((f) => (
              <SelectItem key={f.id} value={f.id}>
                {verbose ? fundPickerLabel(f) : f.fund_name}
              </SelectItem>
            ))}
          </SelectGroup>
        ))}
        {sections.length === 0 && (
          <div className="p-3 text-xs text-muted-foreground">No funds available.</div>
        )}
      </SelectContent>
    </Select>
  );
}
